import { useState } from 'react'

import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ArcadeFieldLabel, ArcadeMoneyInput, CurrencyInput } from '@cash-design-system/react'
import { Money } from '@cash-web/protos/squareup/common/currency.pb'
import { CurrencyCode } from '@cash-web/protos/squareup/common/currency_codes.pb'
import {
  FormBlockerElementMoneyInputElement,
  FormBlockerElementMoneyInputElementDenominationOption,
} from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { formatMoney, GenericMoneyInput, getCurrencyPrefix } from '@cash-web/shared-feature-common'
import { FieldErrorText, FormInput } from '@cash-web/shared-feature-form'
import { convertDecimalMoneyToCents } from '@cash-web/shared-util-formatting'

import { FormBlockerAdditionalElementProps } from '../FormBlockerElement/FormBlockerElement'

export type MoneyInputElementProps = FormBlockerAdditionalElementProps & FormBlockerElementMoneyInputElement

/**
 * Determine prefill amount.
 * If not provided as prop and denomination option is cents, prefill amount should be 0.00
 * This matches mobile behavior to indicate to user only cents can be entered.
 */
const getPrefillAmount = (
  prefill_amount?: Money,
  denomination_option?: FormBlockerElementMoneyInputElementDenominationOption,
  currencyCode?: CurrencyCode
): string => {
  if (prefill_amount?.amount) {
    return formatMoney({
      amount: prefill_amount.amount,
      currency_code: currencyCode,
    })
  }
  if (denomination_option === FormBlockerElementMoneyInputElementDenominationOption.CENTS) {
    return '0.00'
  }
  return ''
}

/**
 * Determine denomination option based on provided props.
 * Use denomination option if provided, then check deprecated props, else default to DOLLARS_AND_CENTS
 */
const getDenominationOption = (
  denomination_option?: FormBlockerElementMoneyInputElementDenominationOption,
  fractional_values_only?: boolean,
  disable_decimal?: boolean
): 'DOLLARS_AND_CENTS' | 'DOLLARS_ONLY' | 'CENTS_ONLY' => {
  if (denomination_option) {
    if (denomination_option === FormBlockerElementMoneyInputElementDenominationOption.DOLLARS) {
      return 'DOLLARS_ONLY'
    }
    if (denomination_option === FormBlockerElementMoneyInputElementDenominationOption.CENTS) {
      return 'CENTS_ONLY'
    }
    return 'DOLLARS_AND_CENTS'
  }

  if (fractional_values_only) {
    return 'CENTS_ONLY'
  }
  if (disable_decimal) {
    return 'DOLLARS_ONLY'
  }

  return 'DOLLARS_AND_CENTS'
}

export const MoneyInputElement = ({
  elementId,
  formSectionIndex,
  shouldAutoFocus,

  // props below from FormBlockerElementMoneyInputElement
  label_text,
  disable_decimal, // to be deprecated but may still be used
  currency_code,
  hint_text,
  is_optional,
  maximum_amount,
  minimum_amount,
  prefill_amount,
  denomination_option,
  fractional_values_only, // to be deprecated but may still be used
}: MoneyInputElementProps) => {
  const { t } = useTranslation()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')
  const { setValue, register, formState, getFieldState } = useFormContext()

  const currencyCode = currency_code || CurrencyCode.USD
  const currencyPrefix = getCurrencyPrefix(currencyCode)

  const maximumAmount = maximum_amount?.amount
  const minimumAmount = minimum_amount?.amount
  const prefillAmount = getPrefillAmount(prefill_amount, denomination_option, currencyCode)
  const [formattedValue, setFormattedValue] = useState(prefillAmount)
  const formFieldName = `${formSectionIndex}.money_input_result.amount.amount`

  const denominationOption = getDenominationOption(denomination_option, fractional_values_only, disable_decimal)

  const { isSubmitted } = formState
  const { error, isDirty } = getFieldState(formFieldName)

  const defaultValueInCents = prefillAmount ? convertDecimalMoneyToCents({ decimalAmount: prefillAmount }) : ''

  const { onBlur, ref, ...inputProps } = register(formFieldName, {
    value: defaultValueInCents,
    required: {
      value: !is_optional,
      message: t('Amount is required.', { context: 'Error message: amount not specified' }),
    },
    validate: (value: string) => {
      const valueAsNumber = Number(value)
      if (minimumAmount != null && valueAsNumber < minimumAmount) {
        return t('Amount must be at least {amount}.', {
          amount: formatMoney({ amount: minimumAmount, currency_code: currencyCode }),
          context: 'Error message: amount is less than minimum allowed amount',
        })
      }
      if (maximumAmount != null && valueAsNumber > maximumAmount) {
        return t('Amount must be less than or equal to {amount}.', {
          amount: formatMoney({ amount: maximumAmount, currency_code: currencyCode }),
          context: 'Error message: amount is more than maximum allowed amount',
        })
      }
      return true
    },
  })

  // if validation error is due to field being required and not present, only show after user attempts to submit
  // for other validation errors (like min/max amount), we want to display immediately if error exists */
  const showError = error && (error?.type !== 'required' || isSubmitted)

  const renderArcadeMoneyInput = () => (
    <ArcadeMoneyInput
      ref={ref}
      {...inputProps}
      aria-label={t('Amount in {CURRENCY}', { context: 'The label for a money input', CURRENCY: currency_code })}
      autoFocus={shouldAutoFocus}
      currencySymbol={currencyPrefix}
      data-testid={elementId}
      denominationOption={denominationOption}
      errorText={showError ? String(error?.message) : undefined}
      isInvalid={showError}
      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
        if (isDirty) {
          onBlur(e)
        }
      }}
      onChange={(value: string) => {
        const valueInCents = value === '' ? '' : convertDecimalMoneyToCents({ decimalAmount: value })
        setFormattedValue(value)
        setValue(formFieldName, valueInCents, { shouldValidate: true, shouldDirty: true })
      }}
      placeholder={hint_text}
      value={formattedValue}
    />
  )

  return (
    <>
      {isArcadeMigrationEnabled ? (
        label_text ? (
          <ArcadeFieldLabel label={label_text}>{renderArcadeMoneyInput()}</ArcadeFieldLabel>
        ) : (
          renderArcadeMoneyInput()
        )
      ) : (
        <GenericMoneyInput
          ariaLabel={t('Amount in {CURRENCY}', { context: 'The label for a money input', CURRENCY: currencyCode })}
          currencyPrefix={currencyPrefix}
          customInput={CurrencyInput}
          data-testid={elementId}
          defaultValue={prefillAmount}
          forceDecimalPoint={!disable_decimal}
          decimalScale={disable_decimal ? 0 : undefined}
          formattedValue={formattedValue}
          isRequired={!is_optional}
          maximumAmount={maximumAmount}
          minimumAmount={minimumAmount}
          name={formFieldName}
          placeholder={hint_text}
          setFormattedValue={setFormattedValue}
          shouldAutoFocus={shouldAutoFocus}
          prefix={undefined} // for Mooncake, CurrencyInput handles prefix
        />
      )}
      <FormInput
        name={`${formSectionIndex}.money_input_result.amount.currency_code`}
        defaultValue={currencyCode}
        type="hidden"
      />
      <FieldErrorText name={formFieldName} />
    </>
  )
}
